import { useApiQuery, type Plan } from 'grabbitbot-legacy-ui'
import { paymentClient } from '../../api'
import { ConfirmButton } from '../admin/adminShared'
import { usePaymentActions, usePaymentBasics, priceText } from './paymentFlow'
import { CheckoutActionButton, CreditBuyPanel, DateTime, PaymentMethodSummary, PaymentNotice, PaymentPage, UtcDateTime } from './paymentShared'
import { failureText } from '../../api/errorMessages'
import { hasPlanEnded, isCanceledButActive, planSessionCause } from './planStatus'

function changeLabel(plan: Plan): string {
  const status = plan.subscription?.status ?? 0
  if (status === 1 || status === 3) return 'Pay one month and save new payment method'
  if (status === 6) return 'Resume, pay one month and save new payment method'
  return 'Subscribe and save new payment method'
}

function planState(plan: Plan) {
  const sub = plan.subscription
  if (!sub) return null
  if (sub.status === 1)
    return (
      <>
        Subscribed, will be automatically renewed at: <DateTime value={sub.expiresAt} />
      </>
    )
  if (sub.status === 3)
    return (
      <>
        Overdue, expires at: <DateTime value={sub.expiresAt} />
      </>
    )
  if (isCanceledButActive(sub))
    return (
      <>
        Canceled, active until <UtcDateTime value={sub.expiresAt} />
      </>
    )
  if (sub.status === 6)
    return (
      <>
        Canceled, expires at: <DateTime value={sub.expiresAt} />
      </>
    )
  return null
}

/**
 * `/payment/method` — legacy `PaymentMethodViewComponent` +
 * `PaymentmethodChangeComponent`: the saved card (brand, last 4, expiry and
 * its warnings), "remove payment method and cancel all active
 * subscriptions", and the ways to change it — every change goes through a
 * Stripe Checkout that saves the new method (a plan payment, or buying
 * credits). No card data is ever entered on this site.
 */
export function PaymentMethodPage() {
  const basics = usePaymentBasics()
  const { generation, refetch, method, billing } = basics
  const actions = usePaymentActions()
  const plans = useApiQuery(() => paymentClient.listPlans(), [generation])
  const planList = Array.isArray(plans.data) ? plans.data : []

  return (
    <PaymentPage title="Payment method">
      <div className="portal-grid-2">
        <section className="portal-card" aria-label="Current payment method">
          <h2>Current payment method</h2>
          {basics.methodLoading && <p>Loading payment method…</p>}
          {basics.methodError && (
            <p className="portal-notice portal-notice--error" role="alert">
              {failureText(basics.methodError, 'Could not load your payment method')}
            </p>
          )}
          {!basics.methodLoading && (!method || !method.setup) && (
            <div>
              <p>Set up your payment method by starting a payment:</p>
              <ul>
                <li>start a subscription</li>
                <li>pay for a products</li>
                <li>buy credit</li>
              </ul>
            </div>
          )}
          {method && method.setup ? (
            <>
              <PaymentMethodSummary method={method} />
              <p>To change your payment method:</p>
              <ul>
                <li>start a subscription with the &quot;Pay with and save new payment method&quot; button</li>
                <li>pay for a products with the &quot;Pay with and save new payment method&quot; button</li>
                <li>buy credit with the &quot;Pay with and save new payment method&quot; button</li>
              </ul>
              <ConfirmButton
                label="Remove payment method and cancel all active subscriptions"
                confirmLabel="Yes, remove it"
                pendingText="Are you sure you want to remove current payment method and cancel all subscriptions?"
                disabled={actions.isBusy('payment:cancel')}
                onConfirm={() => void actions.run('payment:cancel', () => paymentClient.cancelPayment(), refetch)}
              />
              <PaymentNotice feedback={actions.feedbackFor('payment:cancel')} />
            </>
          ) : null}
        </section>

        <section className="portal-card" aria-label="Change payment method">
          <h2>Change payment method</h2>
          <p>Please choose how to change your payment method:</p>
          {planList.length > 0 && (
            <>
              <h3>Pay for a plan</h3>
              <ul className="portal-change-plans">
                {planList.map((plan) => {
                  const key = `pmchange:plan:${plan.id ?? ''}`
                  return (
                    <li key={plan.id ?? plan.name}>
                      <strong>{plan.name}</strong> — {priceText(plan.currency, plan.price)}
                      <div className="portal-text-muted">{planState(plan)}</div>
                      <CheckoutActionButton
                        actions={actions}
                        actionKey={key}
                        label={changeLabel(plan)}
                        create={(oldSessionId) =>
                          paymentClient.createPlanSession(planSessionCause(method, hasPlanEnded(plan)), [plan.id ?? ''], oldSessionId)
                        }
                      />
                      <PaymentNotice feedback={actions.feedbackFor(key)} />
                    </li>
                  )
                })}
              </ul>
            </>
          )}
          <h3>Buy credits</h3>
          <CreditBuyPanel method={method} billing={billing} actions={actions} onChanged={refetch} saveNewMethodOnly />
        </section>
      </div>
    </PaymentPage>
  )
}
